import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Ticket, CheckCircle } from "@phosphor-icons/react";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { PasswordInput } from "../components/ui/password-input";
import { Label } from "../components/ui/label";

export default function ResetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [valid, setValid] = useState(undefined); // undefined=loading
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get(`/auth/reset-password/${token}/info`)
      .then((r) => setValid(!!r.data.valid))
      .catch(() => setValid(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) { setError("Passwords do not match."); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      setTimeout(() => navigate("/login"), 2200);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <div className="w-full max-w-md surface-card shadow-sm p-8">
        <div className="flex items-center gap-2 mb-6">
          <div className="w-9 h-9 rounded-md bg-blue-600 flex items-center justify-center">
            <Ticket weight="fill" size={20} className="text-white" />
          </div>
          <div className="font-display font-bold text-lg tracking-tight">Support By ERP4U</div>
        </div>

        {valid === undefined && <p className="text-sm text-slate-500">Checking link…</p>}

        {valid === false && (
          <>
            <h2 className="font-display text-xl font-bold text-slate-900">Link expired or invalid</h2>
            <p className="text-sm text-slate-500 mt-2">This password reset link is invalid or has expired. Please request a new one.</p>
            <Link to="/forgot-password" className="inline-block mt-5 text-sm text-blue-600 hover:underline font-medium">Request a new link</Link>
          </>
        )}

        {valid && done && (
          <div className="text-center">
            <div className="w-12 h-12 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center mx-auto mb-3">
              <CheckCircle size={24} weight="fill" />
            </div>
            <h2 className="font-display text-xl font-bold text-slate-900">Password updated</h2>
            <p className="text-sm text-slate-500 mt-2">You can now sign in with your new password. Redirecting…</p>
          </div>
        )}

        {valid && !done && (
          <>
            <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900">Set a new password</h2>
            <p className="text-sm text-slate-500 mt-1.5 mb-6">Choose a strong password you don't use elsewhere.</p>
            <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">New password</Label>
                <PasswordInput required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} data-testid="reset-password-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Confirm password</Label>
                <PasswordInput required minLength={6} value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="reset-confirm-input" />
              </div>
              {error && <div className="text-xs text-rose-600 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">{error}</div>}
              <Button type="submit" disabled={loading} className="w-full bg-blue-600 hover:bg-blue-700 text-white" data-testid="reset-submit-button">
                {loading ? "Updating…" : "Update password"}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
